/**
 * Telegram Mini App detection, script loader, and UX helpers.
 * Telegram WebApp SDK is ONLY loaded when explicitly launched within Telegram Mini App
 * via dedicated GET parameters or launch hash, isolating regular web visitors completely.
 */

export const isTelegramMiniApp = () => {
  if (typeof window === 'undefined') return false;

  const search = window.location.search || '';
  const hash = window.location.hash || '';

  // Check for Telegram WebApp URL markers
  const hasMiniAppParam = 
    search.includes('tgWebAppPlatform') ||
    search.includes('tgWebApp=true') ||
    search.includes('tma=1') ||
    search.includes('webapp=1') ||
    hash.includes('tgWebAppData') ||
    hash.includes('tgWebAppThemeParams');

  const hasGlobalTelegram = Boolean(
    window.Telegram?.WebApp?.initData ||
    window.Telegram?.WebApp?.initDataUnsafe?.user
  );

  return Boolean(hasMiniAppParam || hasGlobalTelegram);
};

let scriptLoadingPromise = null;

export const loadTelegramWebAppScript = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.Telegram?.WebApp) return Promise.resolve(window.Telegram.WebApp);

  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve) => {
    // Only load if verified Mini App environment
    if (!isTelegramMiniApp()) {
      return resolve(null);
    }

    const script = document.createElement('script');
    script.id = 'telegram-webapp-sdk';
    script.src = '/js/telegram-web-app.js';
    script.async = true;

    script.onload = () => {
      try {
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.ready();
          window.Telegram.WebApp.expand();
        }
      } catch (err) {
        console.warn('Mini App ready error:', err);
      }
      resolve(window.Telegram?.WebApp || null);
    };

    script.onerror = () => {
      // Fallback if local script failed
      const fallback = document.createElement('script');
      fallback.src = 'https://telegram.org/js/telegram-web-app.js';
      fallback.onload = () => {
        try {
          if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.ready();
            window.Telegram.WebApp.expand();
          }
        } catch (_) {}
        resolve(window.Telegram?.WebApp || null);
      };
      fallback.onerror = () => resolve(null);
      document.head.appendChild(fallback);
    };

    document.head.appendChild(script);
  });

  return scriptLoadingPromise;
};

// Haptic feedback helper
export const triggerHaptic = (type = 'light') => {
  try {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      if (type === 'selection') {
        window.Telegram.WebApp.HapticFeedback.selectionChanged();
      } else if (['success', 'warning', 'error'].includes(type)) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred(type);
      } else {
        window.Telegram.WebApp.HapticFeedback.impactOccurred(type);
      }
    }
  } catch (_) {}
};
